// Utilitaires de calcul de disponibilité réelle, partagés entre
// AvailableVehicles, VehicleCategories et FleetPage.

export interface ReservationRange {
  startDate: string
  endDate: string
}

/**
 * Retourne true si, à l'instant `now`, il est encore possible de démarrer
 * une location aujourd'hui (au moins un créneau non passé).
 * - Journée complète / matin : départ 08:00
 * - Après-midi : départ 13:00
 * Si on est après 13:00, plus aucun créneau réservable aujourd'hui.
 * Le dimanche, aucun départ possible.
 */
export function isTodayStillBookable(now: Date): boolean {
  const day = now.getDay() // 0 = dimanche
  if (day === 0) return false
  const hour = now.getHours()
  const minute = now.getMinutes()
  // Dernier créneau de départ : 13:00 (après-midi)
  if (hour > 13) return false
  if (hour === 13 && minute > 0) return false
  return true
}

/**
 * Retourne le prochain jour "candidat" pour un départ, à partir d'une date.
 * - Avance au lendemain si `fromNextDay` est vrai.
 * - Saute les dimanches (non réservables comme jour de départ).
 * Retourne un Date à 08:00 heure locale.
 */
export function nextBookableDay(from: Date, fromNextDay: boolean): Date {
  const d = new Date(from)
  d.setHours(8, 0, 0, 0)
  if (fromNextDay) {
    d.setDate(d.getDate() + 1)
  }
  // Sauter les dimanches
  while (d.getDay() === 0) {
    d.setDate(d.getDate() + 1)
  }
  return d
}

/**
 * Retourne true si `day` (à 08:00) est couvert par au moins une réservation
 * (start <= day <= end, comparaison sur la date calendaire).
 */
function isDayBooked(day: Date, reservations: ReservationRange[]): boolean {
  const dayStart = new Date(day)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(day)
  dayEnd.setHours(23, 59, 59, 999)

  return reservations.some((r) => {
    const start = new Date(r.startDate)
    const end = new Date(r.endDate)
    // Le jour est réservé s'il chevauche la réservation
    return start <= dayEnd && end >= dayStart
  })
}

export interface VehicleAvailability {
  /** Le véhicule peut-il être réservé pour un départ aujourd'hui ? */
  availableNow: boolean
  /** Prochaine date de départ réellement possible (ISO), ou undefined si disponible maintenant. */
  nextAvailableDate?: string
  /** Date jusqu'à laquelle le véhicule est libre (exclusif), si la prochaine réservation arrive bientôt. */
  bookedUntil?: string
  /** Prochaine réservation bloquante (ISO startDate), si aujourd'hui est le seul jour libre avant. */
  nextReservationStart?: string
}

/**
 * Calcule la vraie disponibilité d'un véhicule à partir de ses réservations.
 *
 * Règles :
 *  1. Si une réservation est actuellement en cours (start <= now <= end),
 *     le véhicule est "en location" → chercher la prochaine date libre après la fin.
 *  2. Si on est après l'heure limite de départ du jour (> 13h), le véhicule
 *     n'est pas réservable aujourd'hui → chercher la prochaine date libre dès demain.
 *  3. Sinon, si le jour suivant (ou aujourd'hui) est libre → disponible maintenant.
 *  4. Saute les jours réservés consécutifs et les dimanches pour trouver le prochain jour réellement libre.
 */
export function computeVehicleAvailability(
  reservations: ReservationRange[],
  now: Date = new Date()
): VehicleAvailability {
  const sorted = [...reservations].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  )

  // 1. Réservation en cours ?
  const currentReservation = sorted.find((r) => {
    const start = new Date(r.startDate)
    const end = new Date(r.endDate)
    return start <= now && end >= now
  })

  const todayBookable = isTodayStillBookable(now)

  // 2. Choix du point de départ pour la recherche du prochain jour libre
  let candidate: Date
  if (currentReservation) {
    // En location → chercher à partir de la fin de la réservation (fusionnée)
    let latestEnd = new Date(currentReservation.endDate)
    for (const r of sorted) {
      const start = new Date(r.startDate)
      const end = new Date(r.endDate)
      if (end <= latestEnd) continue
      const diffHours = (start.getTime() - latestEnd.getTime()) / (1000 * 60 * 60)
      if (diffHours >= 0 && diffHours <= 15) {
        latestEnd = end
      }
    }
    candidate = nextBookableDay(latestEnd, true)
  } else if (!todayBookable) {
    // Aujourd'hui plus réservable (heure passée ou dimanche) → regarder dès demain
    candidate = nextBookableDay(now, true)
  } else {
    // Aujourd'hui encore réservable
    candidate = nextBookableDay(now, false)
  }

  // 3. Avancer tant que le jour candidat est réservé ou est un dimanche
  const MAX_ITERATIONS = 365
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    if (candidate.getDay() === 0) {
      candidate.setDate(candidate.getDate() + 1)
      continue
    }
    if (isDayBooked(candidate, sorted)) {
      candidate.setDate(candidate.getDate() + 1)
      continue
    }
    break
  }

  // 4. Le candidat est-il aujourd'hui ? → disponible maintenant
  const todayStr = now.toISOString().slice(0, 10)
  const candidateStr = candidate.toISOString().slice(0, 10)

  if (candidateStr === todayStr && todayBookable && !currentReservation) {
    // Aujourd'hui est libre : vérifier si une réservation commence très bientôt
    // (aujourd'hui soir, demain matin, ou dans les prochains jours sans gap réservable)
    const upcomingReservation = sorted.find((r) => {
      const start = new Date(r.startDate)
      return start > now
    })

    if (upcomingReservation) {
      const nextStart = new Date(upcomingReservation.startDate)
      // Jour de début de la prochaine réservation (à 00:00 local pour comparaison jour)
      const nextStartDay = new Date(nextStart)
      nextStartDay.setHours(0, 0, 0, 0)
      const todayMidnight = new Date(now)
      todayMidnight.setHours(0, 0, 0, 0)
      const daysUntilNext = Math.round(
        (nextStartDay.getTime() - todayMidnight.getTime()) / (1000 * 60 * 60 * 24)
      )

      // Si la prochaine réservation démarre aujourd'hui (plus tard dans la journée)
      // ou demain, on considère le véhicule comme quasi-indisponible.
      if (daysUntilNext <= 1) {
        return {
          availableNow: true,
          bookedUntil: nextStart.toISOString(),
          nextReservationStart: nextStart.toISOString(),
        }
      }
    }

    return { availableNow: true }
  }

  return {
    availableNow: false,
    nextAvailableDate: candidate.toISOString(),
  }
}
